import { LoaderFunctionArgs } from "@remix-run/node";
import { makeAuthHeaders } from "~/api/endpoints/Auth";
import Api from "~/api/api";
import { commitSession, getAuthSession, getSession } from "~/sessions";
import invariant from "tiny-invariant";
import {
  json,
  Outlet,
  ShouldRevalidateFunction,
  useLoaderData,
} from "@remix-run/react";
import { getOrderTypeCode, orderTypeCodeValid } from "~/helpers/orderHelper";
import { urlOrderSource } from "~/routes/$orderSource";
import { PromotionContextProvider } from "~/contexts/PromotionContextProvider";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));

  invariant(params.siteId, "expected params.siteId");
  invariant(params.orderSource, "expected params.orderSource");

  const siteInfo = await Api.Site.getSite(request, params.siteId);
  const orderSource = urlOrderSource[params.orderSource];

  let orderTypeCode = session.get("orderTypeCode");
  if (
    !orderTypeCode ||
    !orderTypeCodeValid(siteInfo.site, orderSource, orderTypeCode)
  ) {
    orderTypeCode = getOrderTypeCode(orderSource, siteInfo.site.orderTypes);
    session.set("orderTypeCode", orderTypeCode);
  }

  invariant(orderTypeCode, "expected orderTypeCode");

  const authHeaders = makeAuthHeaders(request, authSession.get("accessToken"));

  const scheduledPromotions = await Api.Promotions.getScheduledPromotions(
    authHeaders,
    siteInfo.orgId,
    params.siteId,
    new Date().toISOString(),
    orderTypeCode,
  );

  return json(
    {
      scheduledPromotions,
    },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
      },
    },
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formAction,
  currentUrl,
  actionResult,
}) => {
  return (
    formAction === "/auth/requestPasswordless" ||
    formAction === "/auth/verify" ||
    formAction === "/auth/signup" ||
    formAction === "/auth/logout" ||
    formAction === "/auth/myAccount" ||
    formAction === "/loyalty/purchaseAsset" ||
    (formAction != undefined &&
      formAction?.includes("/payment") &&
      !actionResult?.preventRevalidation) ||
    currentUrl.pathname.includes("success")
  );
};

export default function Page() {
  const loaderData = useLoaderData<typeof loader>();

  return (
    <PromotionContextProvider
      scheduledPromotions={loaderData.scheduledPromotions}
    >
      <Outlet />
    </PromotionContextProvider>
  );
}
